<script setup lang="ts">
import { computed, inject, ref } from "vue";

import GlobalPopper, { PopperType } from "~/components/globalPopper";
import { ENTITY_CARD_PREVIEW_SHOW_DELAY_MS } from "~/constants/app";
import { DOC_PREVIEW, SKILL_CARD, TASK_CARD, USER_CARD } from "~/constants/injection";
import { Placement } from "~/shared/enums";
import { useDataStore, usePageStore } from "~/stores";
import { useAsyncDoc, useAsyncTask } from "~/utils/entityIncrementalLoading";
import { isInaccessibleAgent } from "~/utils/user";

const SkillCard = inject(SKILL_CARD);
const TaskCard = inject(TASK_CARD);
const UserCard = inject(USER_CARD);

const props = defineProps<{
  taskDuid?: string;
  docDuid?: string;
  skillDuid?: string;
  userDuid?: string;
  block?: boolean;
  heightBlock?: boolean;
  skidding?: number;
  distance?: number;
  placement?: Placement;
  shown?: boolean;
  triggers?: string[];
  referenceSelector?: string;
  disabled?: boolean;
}>();

const emit = defineEmits<{
  click: [event: Event];
}>();

const dataStore = useDataStore();
const pageStore = usePageStore();

const tooltip = ref<InstanceType<typeof GlobalPopper> | null>(null);

const popperControlProps = computed(() => ({
  ...(props.shown === undefined ? {} : { shown: props.shown }),
  ...(props.triggers === undefined ? {} : { triggers: props.triggers }),
}));

const { doc } = useAsyncDoc(computed(() => props.docDuid));
const { task } = useAsyncTask(computed(() => props.taskDuid));
const skill = computed(() => (props.skillDuid ? dataStore.getSkillByDuid(props.skillDuid) : undefined));
const user = computed(() => (props.userDuid ? dataStore.getUserByDuid(props.userDuid) : undefined));

const hasNothingToShow = computed(
  () => !props.taskDuid && !props.docDuid && !props.skillDuid && (!user.value || isInaccessibleAgent(user.value))
);

const getReferenceNode = () =>
  props.referenceSelector ? (tooltip.value?.triggerRef?.closest(props.referenceSelector) ?? null) : null;

const DocPreview = inject(DOC_PREVIEW);
</script>

<template>
  <GlobalPopper
    ref="tooltip"
    :type="PopperType.TOOLTIP"
    no-auto-focus
    :disabled="disabled || hasNothingToShow || pageStore.isMobile"
    :placement="placement ?? Placement.LEFT"
    :skidding="skidding"
    :distance="distance"
    :delay="{ show: ENTITY_CARD_PREVIEW_SHOW_DELAY_MS, hide: 0 }"
    :auto-hide="task ? task.title !== '' : doc ? doc.title !== '' : skill ? skill.title !== '' : Boolean(user)"
    :reference-node="referenceSelector === undefined ? undefined : getReferenceNode"
    :theme="`tooltip-arrowless-${pageStore.theme}`"
    v-bind="popperControlProps"
    class="flex min-w-0"
    :class="{
      'max-w-full grow': block,
      'h-full': heightBlock,
    }"
    @click="emit('click', $event)"
    @keydown.enter="emit('click', $event)">
    <slot />

    <template #popper>
      <div class="w-64 rounded-sm border border-md bg-std" :class="[task && 'px-2 pt-2', user && 'p-3']">
        <TaskCard v-if="task" :task="task" include-dartboard noninteractive />
        <DocPreview v-if="docDuid" :duid="docDuid" hide-remove borderless />
        <SkillCard v-if="skill" :skill="skill" />
        <UserCard v-if="user" :user="user" />
      </div>
    </template>
  </GlobalPopper>
</template>

<style>
.v-popper--theme-tooltip-arrowless-light .v-popper__arrow-container,
.v-popper--theme-tooltip-arrowless-dark .v-popper__arrow-container {
  display: none;
}
.v-popper--theme-tooltip-arrowless-light .v-popper__wrapper,
.v-popper--theme-tooltip-arrowless-dark .v-popper__wrapper {
  box-shadow:
    0 10px 15px -3px rgb(0 0 0 / 0.1),
    0 4px 6px -4px rgb(0 0 0 / 0.1);
}
.v-popper--theme-tooltip-arrowless-light .v-popper__inner,
.v-popper--theme-tooltip-arrowless-dark .v-popper__inner {
  border-width: 0px;
  border-radius: 4px;
  background-color: transparent;
}
</style>
